import React, { useEffect, useRef, useState } from "react";
import { formatUsd, inlinePauseMarker, LAUNCH_SECTION_COUNT } from "@veyso/core";
import { scriptParts, wordCount } from "./studio.js";
import { StudioError, type PostError } from "./RevisionChat.js";

/**
 * The voice-over, editable.
 *
 * Edited words are read exactly as written and the picture is rebuilt around
 * them, as a new version. Nothing happens until Make is pressed, so the panel
 * says plainly when the box holds words that are not in any version yet.
 *
 * A pause is written into the words as `[1s]`, as in a product demo's script:
 * the voice holds it, and the picture and the subtitles never see it.
 */
export const ScriptPanel: React.FC<{
  script: string;
  budget: number;
  /** Words a second the narrator speaks; a pause counts as the words it would hold. */
  pace?: number;
  quoteUsd: number;
  nextNumber: number;
  busy: boolean;
  /** A version is being made; its script replaces the box when it lands. */
  making: boolean;
  /** Why nothing can be made right now; null when it can. */
  disabledReason: string | null;
  error: PostError | null;
  onMake: (script: string) => Promise<boolean>;
}> = ({ script, budget, pace, quoteUsd, nextNumber, busy, making, disabledReason, error, onMake }) => {
  const [draft, setDraft] = useState(script);
  const shown = useRef(script);
  const box = useRef<HTMLTextAreaElement>(null);

  /**
   * A one-second pause at the caret, changed by retyping the number.
   *
   * `insertText` rather than setting the value, so ⌘Z takes it back like any
   * typing; `setRangeText` where a browser will not insert that way.
   */
  const insertPause = () => {
    const field = box.current;
    if (!field || busy) return;
    field.focus();
    const before = field.value.slice(0, field.selectionStart);
    const after = field.value.slice(field.selectionEnd);
    const text = `${/\s$|^$/.test(before) ? "" : " "}${inlinePauseMarker(1)}${/^\s/.test(after) ? "" : " "}`;
    if (!document.execCommand("insertText", false, text)) {
      field.setRangeText(text, field.selectionStart, field.selectionEnd, "end");
      setDraft(field.value);
    }
  };

  // A new version brings a new script. It replaces the box only when the box
  // still held the old one: edits somebody is part-way through are theirs.
  useEffect(() => {
    if (shown.current === script) return;
    setDraft((current) => (current === shown.current ? script : current));
    shown.current = script;
  }, [script]);

  const words = wordCount(draft, pace);
  const over = budget > 0 && words > budget;
  const parts = scriptParts(draft);
  const partsOff = parts > 0 && parts !== LAUNCH_SECTION_COUNT;
  // While a version is being made the box holds the words sent for it, which
  // differ from the newest finished script without being an unsent edit.
  const edited = draft.trim() !== script.trim();
  const unsent = edited && !making;
  const blocked = busy || disabledReason !== null;

  const make = async () => {
    if (await onMake(draft.trim())) {
      // The made version's script arrives with it; until then the box keeps
      // the words that were sent rather than snapping back to the old ones.
      shown.current = draft;
    }
  };

  return (
    <section className="flex h-[640px] min-w-0 flex-col overflow-hidden rounded-[14px] border border-[var(--edge)] bg-bay-850 lg:h-[760px]">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--edge)] px-4 py-3.5">
        <div className="flex flex-col gap-0.5">
          <h2 className="text-[14px] font-semibold text-ink-100">Voice-over script</h2>
          <span className="text-[12px] text-ink-300">
            Edit the words directly. The voice and the picture are remade to match. For a pause, type{" "}
            <code className="font-mono text-ink-200">[1s]</code> or press ⌘K (up to 3s). Keep a blank line between the
            film's {LAUNCH_SECTION_COUNT} parts so each lines up with its section.
          </span>
        </div>
        <span className="tnum flex gap-3 text-[12px]">
          <span
            className={partsOff ? "font-medium text-signal-warn" : "text-ink-300"}
            title={
              partsOff
                ? `With ${LAUNCH_SECTION_COUNT} parts, each one lines up with its section of the film.`
                : undefined
            }
          >
            {parts} / {LAUNCH_SECTION_COUNT} parts
          </span>
          <span className={over ? "font-medium text-signal-warn" : "text-ink-300"}>
            {words} / {budget} words
          </span>
        </span>
      </div>

      <label htmlFor="launch-script" className="sr-only">
        Voice-over script
      </label>
      <textarea
        id="launch-script"
        ref={box}
        className="flex-1 resize-none bg-bay-850 p-4 text-[14px] leading-[1.65] text-ink-100 outline-none focus-visible:ring-2 focus-visible:ring-[var(--edge-focus)] focus-visible:ring-inset"
        value={draft}
        disabled={busy}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "k") return;
          event.preventDefault();
          insertPause();
        }}
      />

      {error ? (
        <div className="border-t border-[var(--edge)] px-4 py-3">
          <StudioError error={error} />
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--edge)] bg-bay-900 px-4 py-3">
        <span className={`text-[12px] ${unsent || over ? "font-medium text-signal-warn" : "text-ink-400"}`}>
          {over
            ? `Longer than about ${budget} words runs past the end of the film.`
            : unsent
              ? "Edited — not made into a version yet"
              : (disabledReason ?? "Matches the newest version.")}
        </span>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy}
            title="Hold the voice for a second here (⌘K)"
            // A textarea keeps its caret when it loses focus, so the pause
            // lands where the writer left off.
            onClick={insertPause}
          >
            ⏸ Add pause
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            // Not while a version is being made: discarding then would leave
            // the box on the old script, and the new one would not replace it.
            disabled={!edited || busy || making}
            onClick={() => setDraft(script)}
          >
            Discard edits
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={blocked || !edited || over || words === 0}
            title={disabledReason ?? undefined}
            onClick={() => void make()}
          >
            Make v{nextNumber} · {quoteUsd > 0 ? formatUsd(quoteUsd) : "Free"}
          </button>
        </div>
      </div>
    </section>
  );
};
